import { useLocation, useNavigate, useSearchParams } from '@remix-run/react';
import { useEffect } from 'react';

import type { SetCookieOptions } from './useUtils/useCookies';

interface Props {
  setCookie: (options: SetCookieOptions) => void;
  cookiesSet: boolean;
  guid?: string;
}

// Hook for setting the `last_resource_guid` cookie. This cookie is used when a subscription is upgraded or
// downgraded so that it can be attributed back to the specified resource. I.e. so that a particular upgrade
// can be attributed to a particular post.
//
// It prioritizes the `last_resource_guid` query parameter, otherwise will use the value that is passed in
// if present. Relying on a query parameter allows us to set the value to resources that may not have a web
// version. I.e. if someone were to downgrade coming from an automation email or welcome email.
const useSetLastViewedResource = ({ guid, setCookie, cookiesSet }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const value = searchParams.get('last_resource_guid') || guid;
  const hasFragmentIdentifier = location.hash !== '';

  // save current search params string to compare later
  const searchParamsString = searchParams.toString();
  useEffect(() => {
    if (cookiesSet && value) {
      setCookie({
        name: 'last_resource_guid',
        value,
        days: 1,
      });

      searchParams.delete('last_resource_guid');

      if (hasFragmentIdentifier) {
        const newSearchParams = new URLSearchParams(searchParams);
        const newUrl = `${location.pathname}?${newSearchParams.toString()}${location.hash}`;
        navigate(newUrl, { replace: true, state: {} });
      } else {
        // If the search params have changed, update them
        // this causes a navigation, so we only want to do it they change
        // otherwise we're simply reloading the page for no reason
        if (searchParamsString !== searchParams.toString()) {
          setSearchParams(searchParams, { replace: true });
        }
      }
    }
  }, [cookiesSet, value]);
};

export default useSetLastViewedResource;
